// FreLancTrac — mobile PWA for Freelancers, Managers, Operation Heads and SuperAdmins.
// Reuses the existing JWT (`fms_token`) and `/api/*` endpoints. No client-portal screens here.
import RichText from "@/components/app/RichText";
import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, statusPalette } from "@/lib/statusColor";
import PhaseTracker from "@/components/app/PhaseTracker";
import {
  Home as HomeIcon, FolderKanban, ClipboardList, MessageSquare, FileText, Ticket,
  ListChecks, LogOut, ChevronLeft, ChevronRight, Send, CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";

const ROYAL = "#1B4332";
const ROYAL_DARK = "#132E24";
const GOLD = "#B8974A";
const CREAM = "#F6F2E8";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const isAdmin = (role) => role === "SuperAdmin" || role === "TeamLeader";
const isManager = (role) => role === "Manager";
const isFreelancer = (role) => role === "Freelancer";

const ROLE_LABEL = {
  SuperAdmin: "Super Admin",
  TeamLeader: "Operation Head",
  Manager: "Manager",
  Freelancer: "Freelancer",
};

/* ---------------- Login ---------------- */
export function StaffLogin() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { document.title = "FreLancTrac — Sign in"; }, []);
  useEffect(() => { if (user) nav("/app", { replace: true }); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) nav("/app", { replace: true });
    else setError(r.error);
  };

  return (
    <div className="min-h-[100dvh] flex flex-col px-6 pt-12 pb-8" data-testid="staff-login"
      style={{ background: `linear-gradient(160deg, ${ROYAL} 0%, ${ROYAL_DARK} 60%, #0E2219 100%)`, color: CREAM }}>
      <Link to="/" className="font-display text-3xl leading-none" data-testid="staff-login-brand">
        FreLancTrac<span style={{ color: GOLD }}>.</span>
      </Link>
      <div className="mt-10 mb-6">
        <div className="label-caps" style={{ color: `${GOLD}` }}>Sign in</div>
        <h1 className="font-display text-4xl mt-1">Welcome back.</h1>
        <p className="mt-2 text-sm" style={{ color: "rgba(246,242,232,0.75)" }}>
          Projects, work orders and tickets for your studio — on your phone.
        </p>
      </div>

      <form onSubmit={submit} className="w-full" data-testid="staff-login-form">
        <label className="label-caps block mb-1">Email</label>
        <Input data-testid="staff-email" type="email" required inputMode="email" autoComplete="email"
          value={email} onChange={(e) => setEmail(e.target.value)}
          className="rounded-lg h-11 mb-4 bg-white text-ink focus-visible:ring-0" />
        <label className="label-caps block mb-1">Password</label>
        <Input data-testid="staff-password" type="password" required autoComplete="current-password"
          value={password} onChange={(e) => setPassword(e.target.value)}
          className="rounded-lg h-11 bg-white text-ink focus-visible:ring-0" />
        {error && (
          <div data-testid="staff-login-error" className="mt-4 border border-[#B02A00] bg-white text-[#B02A00] px-3 py-2 text-sm rounded-lg">
            {error}
          </div>
        )}
        <button data-testid="staff-login-submit" disabled={busy}
          className="mt-6 w-full h-11 rounded-full font-semibold text-sm tracking-wide transition-all disabled:opacity-60 hover:brightness-110 active:scale-[0.99]"
          style={{ background: GOLD, color: ROYAL_DARK, boxShadow: "0 10px 24px -10px rgba(0,0,0,0.5)" }}>
          {busy ? "Signing in…" : "Sign in →"}
        </button>
      </form>

      <div className="mt-auto pt-10 text-[11px] font-mono-tight uppercase tracking-widest" style={{ color: "rgba(246,242,232,0.55)" }}>
        FreLancTrac · Studio Operations
      </div>
    </div>
  );
}

/* ---------------- Shell + role-based bottom nav ---------------- */
function AuthGuard({ children }) {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/app/login", { replace: true }); }, [user, loading, nav]);
  if (loading || !user) {
    return <div className="min-h-[100dvh] flex items-center justify-center" style={{ background: CREAM }}>
      <div className="text-sm" style={{ color: `${ROYAL}99` }}>Loading…</div>
    </div>;
  }
  return children;
}

function navForRole(role) {
  if (isFreelancer(role)) return [
    { to: "/app", icon: HomeIcon, label: "Home", end: true, testid: "snav-home" },
    { to: "/app/work-orders", icon: ClipboardList, label: "Work", testid: "snav-work" },
    { to: "/app/projects", icon: FolderKanban, label: "Projects", testid: "snav-projects" },
    { to: "/app/tickets", icon: Ticket, label: "Tickets", testid: "snav-tickets" },
    { to: "/app/files", icon: FileText, label: "Files", testid: "snav-files" },
  ];
  if (isManager(role)) return [
    { to: "/app", icon: HomeIcon, label: "Home", end: true, testid: "snav-home" },
    { to: "/app/work-orders", icon: ClipboardList, label: "Work", testid: "snav-work" },
    { to: "/app/projects", icon: FolderKanban, label: "Projects", testid: "snav-projects" },
    { to: "/app/tickets", icon: Ticket, label: "Tickets", testid: "snav-tickets" },
    { to: "/app/messages", icon: MessageSquare, label: "Messages", testid: "snav-messages" },
  ];
  // SuperAdmin / TeamLeader (Operation Head)
  return [
    { to: "/app", icon: HomeIcon, label: "Home", end: true, testid: "snav-home" },
    { to: "/app/projects", icon: FolderKanban, label: "Projects", testid: "snav-projects" },
    { to: "/app/work-orders", icon: ClipboardList, label: "Work", testid: "snav-work" },
    { to: "/app/tasks", icon: ListChecks, label: "Tasks", testid: "snav-tasks" },
    { to: "/app/messages", icon: MessageSquare, label: "Messages", testid: "snav-messages" },
  ];
}

function StaffHeader({ user, title, back }) {
  const nav = useNavigate();
  const { logout } = useAuth();
  const doLogout = async () => { await logout(); nav("/app/login", { replace: true }); };
  return (
    <header className="sticky top-0 z-30 px-4 pt-[max(env(safe-area-inset-top),0.5rem)] pb-3"
      style={{ background: ROYAL, color: CREAM, borderBottom: `1px solid ${GOLD}66` }}
      data-testid="staff-header">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          {back ? (
            <button data-testid="staff-back" onClick={() => nav(-1)} className="p-1 -ml-1 rounded-full" aria-label="Back">
              <ChevronLeft size={22} />
            </button>
          ) : (
            <div className="font-display text-lg leading-none">
              FreLancTrac<span style={{ color: GOLD }}>.</span>
            </div>
          )}
          <div className="min-w-0">
            <div className="label-caps truncate" style={{ color: GOLD }}>{ROLE_LABEL[user.role] || user.role}</div>
            <div className="font-display text-lg leading-tight truncate" data-testid="staff-page-title">
              {title}
            </div>
          </div>
        </div>
        <button data-testid="staff-logout" onClick={doLogout}
          className="inline-flex items-center gap-1 text-[11px] font-mono-tight uppercase tracking-widest px-2.5 py-1.5 rounded-full"
          style={{ background: `${GOLD}22`, color: CREAM, border: `1px solid ${GOLD}66` }}>
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </header>
  );
}

function StaffBottomNav({ nav }) {
  return (
    <nav className="fixed bottom-0 inset-x-0 z-30" data-testid="staff-bottom-nav"
      style={{ background: ROYAL, borderTop: `1px solid ${GOLD}66`, paddingBottom: "max(env(safe-area-inset-bottom), 0.25rem)" }}>
      <ul className="max-w-md mx-auto grid grid-cols-5">
        {nav.map(({ to, icon: Icon, label, end, testid }) => (
          <li key={to}>
            <NavLink to={to} end={end} data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono-tight uppercase tracking-widest transition-colors ${isActive ? "" : "opacity-70 hover:opacity-100"}`}
              style={({ isActive }) => ({ color: isActive ? GOLD : CREAM })}>
              <Icon size={18} strokeWidth={2} />
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function StaffShell() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/staff-sw.js", { scope: "/app" }).catch(() => {});
    }
    if ("Notification" in window && Notification.permission === "default") {
      try { Notification.requestPermission(); } catch {}
    }
  }, []);

  const nav = useMemo(() => (user ? navForRole(user.role) : []), [user]);
  const back = /^\/app\/(work-orders|projects|tickets|tasks)\/[^/]+$/.test(location.pathname);
  const titleMap = {
    "/app": "Home",
    "/app/work-orders": "Work Orders",
    "/app/projects": "Projects",
    "/app/tickets": "Tickets",
    "/app/files": "Files",
    "/app/messages": "Messages",
    "/app/tasks": "Management Tasks",
  };
  const title = useMemo(() => {
    const path = location.pathname;
    if (/^\/m\/work-orders\/[^/]+/.test(path)) return "Work Order";
    if (/^\/m\/projects\/[^/]+/.test(path)) return "Project";
    if (/^\/m\/tickets\/[^/]+/.test(path)) return "Ticket";
    if (/^\/m\/tasks\/[^/]+/.test(path)) return "Task";
    return titleMap[path] || "FreLancTrac";
  }, [location.pathname]);

  useEffect(() => { document.title = `FreLancTrac — ${title}`; }, [title]);

  return (
    <AuthGuard>
      <div className="min-h-[100dvh] flex flex-col" style={{ background: CREAM }}>
        <StaffHeader user={user || {}} title={title} back={back} />
        <main className="flex-1 max-w-md w-full mx-auto px-4 pt-4 pb-28">
          <Outlet />
        </main>
        <StaffBottomNav nav={nav} />
      </div>
    </AuthGuard>
  );
}

/* ---------------- Home ---------------- */
export function StaffHome() {
  const { user } = useAuth();
  const [wos, setWos] = useState(null);
  const [projects, setProjects] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    api.get("/work-orders").then((r) => setWos(r.data)).catch(() => setWos([]));
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    if (isAdmin(user.role)) {
      api.get("/management-tasks").then((r) => setTasks(r.data)).catch(() => setTasks([]));
    }
  }, [user.role]);

  const today = new Date().toISOString().slice(0, 10);
  const submitted = (wos || []).filter((w) => w.status === "Submitted");
  const inProg = (wos || []).filter((w) => w.status === "In Progress" || w.status === "Assigned");
  const overdueProjects = (projects || []).filter((p) => p.due_date && p.due_date < today && p.status !== "Completed");
  const activeProjects = (projects || []).filter((p) => (p.status || "").toLowerCase() !== "completed");
  const openTickets = (tickets || []).filter((t) => !t.closed && t.status !== "Closed");

  return (
    <div data-testid="staff-home">
      {/* Hero summary */}
      <div className="rounded-2xl p-4" style={{ background: ROYAL, color: CREAM, border: `1px solid ${GOLD}66` }} data-testid="staff-home-summary">
        <div className="label-caps" style={{ color: GOLD }}>
          {isFreelancer(user.role) ? "Your focus" : isManager(user.role) ? "Today's focus" : "Studio pulse"}
        </div>
        <div className="font-display text-2xl mt-1">Hi, {(user.name || "there").split(" ")[0]}.</div>
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          {isFreelancer(user.role) && (
            <>
              <HomeStat label="Open WOs" value={inProg.length} />
              <HomeStat label="Submitted" value={submitted.length} />
              <HomeStat label="Tickets" value={openTickets.length} />
            </>
          )}
          {isManager(user.role) && (
            <>
              <HomeStat label="To review" value={submitted.length} />
              <HomeStat label="Projects" value={activeProjects.length} />
              <HomeStat label="Overdue" value={overdueProjects.length} warn />
            </>
          )}
          {isAdmin(user.role) && (
            <>
              <HomeStat label="Projects" value={activeProjects.length} />
              <HomeStat label="To review" value={submitted.length} />
              <HomeStat label="Overdue" value={overdueProjects.length} warn />
            </>
          )}
        </div>
      </div>

      {/* Role-specific lists */}
      {isFreelancer(user.role) && (
        <>
          <Section title="In progress">
            {inProg.length === 0 ? <EmptyLine text="Nothing active — enjoy the breather." /> :
              inProg.slice(0, 4).map((w) => <WorkOrderRow key={w.id} w={w} />)}
          </Section>
          <Section title={`Submitted (${submitted.length})`}>
            {submitted.length === 0 ? <EmptyLine text="No submissions pending review." /> :
              submitted.slice(0, 4).map((w) => <WorkOrderRow key={w.id} w={w} />)}
          </Section>
        </>
      )}
      {isManager(user.role) && (
        <>
          <Section title={`Needs your review (${submitted.length})`} tone="warn">
            {submitted.length === 0 ? <EmptyLine text="Nothing to review right now." /> :
              submitted.slice(0, 5).map((w) => <WorkOrderRow key={w.id} w={w} />)}
          </Section>
          <Section title="Your projects">
            {activeProjects.slice(0, 4).map((p) => <ProjectRow key={p.id} p={p} />)}
            {activeProjects.length === 0 && <EmptyLine text="No active projects assigned." />}
          </Section>
        </>
      )}
      {isAdmin(user.role) && (
        <>
          <Section title={`Submitted work orders (${submitted.length})`} tone="warn">
            {submitted.slice(0, 5).map((w) => <WorkOrderRow key={w.id} w={w} />)}
            {submitted.length === 0 && <EmptyLine text="Nothing to review right now." />}
          </Section>
          <Section title={`Overdue projects (${overdueProjects.length})`} tone="warn">
            {overdueProjects.slice(0, 5).map((p) => <ProjectRow key={p.id} p={p} />)}
            {overdueProjects.length === 0 && <EmptyLine text="All projects are on track." />}
          </Section>
          {tasks && tasks.length > 0 && (
            <Section title="Open management tasks">
              {tasks.filter((t) => t.status !== "Completed").slice(0, 4).map((t) => <TaskRow key={t.id} t={t} />)}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

function HomeStat({ label, value, warn }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "rgba(246,242,232,0.7)" }}>{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color: warn && value > 0 ? "#FFC7A6" : CREAM }}>{value}</div>
    </div>
  );
}
function Section({ title, children, tone }) {
  return (
    <section className="mt-5">
      <div className="label-caps mb-2 flex items-center gap-2"
        style={{ color: tone === "warn" ? "#7A2B00" : ROYAL }}>
        {tone === "warn" && <AlertTriangle size={11} />}
        {title}
      </div>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}
function EmptyLine({ text }) {
  return <li className="text-sm text-center py-5" style={{ color: `${ROYAL}80` }}>{text}</li>;
}

function WorkOrderRow({ w }) {
  return (
    <li>
      <Link to={`/app/work-orders/${w.id}`} data-testid={`staff-wo-${w.id}`}
        className="block bg-white rounded-xl p-3 border transition-all active:scale-[0.995]"
        style={{ borderColor: `${ROYAL}1a` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>{w.title}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${ROYAL}80` }}>
              Due {fmtDate(w.deadline)}{w.freelancer_name ? ` · ${w.freelancer_name}` : ""}
            </div>
          </div>
          <StatusBadge status={w.status} />
        </div>
      </Link>
    </li>
  );
}

function ProjectRow({ p }) {
  return (
    <li>
      <Link to={`/app/projects/${p.id}`} data-testid={`staff-project-${p.id}`}
        className="block bg-white rounded-xl p-3 border"
        style={{ borderColor: `${ROYAL}1a` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>{p.name}</div>
            <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${ROYAL}80` }}>
              {p.project_number || "—"} · due {fmtDate(p.due_date)}
            </div>
          </div>
          <StatusBadge status={p.status} />
        </div>
      </Link>
    </li>
  );
}

function TaskRow({ t }) {
  const overdue = t.deadline && t.deadline < new Date().toISOString().slice(0, 10) && t.status !== "Completed";
  return (
    <li className="bg-white rounded-xl p-3 border" style={{ borderColor: `${ROYAL}1a` }} data-testid={`staff-task-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>{t.title}</div>
          <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: overdue ? "#7A2B00" : `${ROYAL}80` }}>
            {overdue ? "Overdue · " : ""}Due {fmtDate(t.deadline)}
          </div>
        </div>
        <StatusBadge status={t.status} />
      </div>
    </li>
  );
}

/* ---------------- Work Orders ---------------- */
export function StaffWorkOrders() {
  const { user } = useAuth();
  const [wos, setWos] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/work-orders").then((r) => setWos(r.data)).catch(() => setWos([])); }, []);
  if (wos === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  const filtered = filter === "all" ? wos : wos.filter((w) => w.status === filter);
  // Submitted at the top for managers/admins
  const sorted = isFreelancer(user.role) ? filtered : [...filtered].sort((a, b) => {
    const pri = (s) => (s === "Submitted" ? 0 : s === "In Progress" ? 1 : s === "Assigned" ? 2 : 3);
    return pri(a.status) - pri(b.status);
  });
  const statusOpts = ["all", "Assigned", "In Progress", "Submitted", "Completed"];
  return (
    <div data-testid="staff-wos">
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 mb-2">
        {statusOpts.map((s) => (
          <button key={s} data-testid={`staff-wo-filter-${s}`} onClick={() => setFilter(s)}
            className="text-[11px] font-mono-tight uppercase tracking-widest px-3 py-1.5 rounded-full whitespace-nowrap"
            style={{ background: filter === s ? ROYAL : "transparent", color: filter === s ? CREAM : ROYAL, border: `1px solid ${filter === s ? ROYAL : `${ROYAL}30`}` }}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>
      <ul className="space-y-2">
        {sorted.map((w) => <WorkOrderRow key={w.id} w={w} />)}
        {sorted.length === 0 && <li className="text-sm text-center py-8" style={{ color: `${ROYAL}80` }}>No work orders.</li>}
      </ul>
    </div>
  );
}

const FREELANCER_STATUSES = ["Assigned", "In Progress", "Submitted"];
const MANAGER_STATUSES = ["Assigned", "In Progress", "Submitted", "Reassigned", "Completed", "Cancelled"];

export function StaffWorkOrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [project, setProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: wos } = await api.get("/work-orders");
      const found = wos.find((x) => x.id === id);
      if (!found) { toast.error("Not found"); nav("/app/work-orders"); return; }
      setW(found);
      try { const { data: p } = await api.get(`/projects/${found.project_id}`); setProject(p); } catch {}
      try { const { data: cs } = await api.get(`/projects/${found.project_id}/comments`); setComments(cs.filter((c) => (c.work_order_id || null) === id || !c.work_order_id || c.body?.includes(`[WO:${id}]`))); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (s) => {
    setBusy(true);
    try { await api.patch(`/work-orders/${id}`, { status: s }); toast.success(`Marked ${s}`); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const addComment = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try {
      await api.post(`/projects/${w.project_id}/comments`,
        { body: `[WO:${id}] ${note.trim()}`, client_visible: false });
      setNote(""); await load();
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  if (!w) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;

  const canFl = isFreelancer(user.role);
  const statusChoices = canFl ? FREELANCER_STATUSES : MANAGER_STATUSES;

  return (
    <div data-testid="staff-wo-detail">
      <div className="rounded-2xl p-4" style={{ background: ROYAL, color: CREAM, border: `1px solid ${GOLD}66` }}>
        <div className="label-caps" style={{ color: GOLD }}>Work Order</div>
        <div className="font-display text-xl mt-1 leading-tight">{w.title}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <StatusBadge status={w.status} testid={`staff-wo-status-${w.id}`} />
          <span className="text-[11px] font-mono-tight uppercase tracking-widest opacity-80">Due {fmtDate(w.deadline)}</span>
        </div>
        {project && (
          <div className="mt-2 text-xs opacity-85">
            <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Project · </span>
            <Link to={`/app/projects/${project.id}`} className="underline">{project.name}</Link>
          </div>
        )}
        {(w.description_html || w.description) && (
          <RichText html={w.description_html} text={w.description} testid="staff-wo-description" className="mt-3 text-sm" />
        )}
        {!canFl && (
          <div className="mt-2 text-xs opacity-85">
            <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Freelancer · </span>
            {w.freelancer_name || w.freelancer_id}
          </div>
        )}
        {w.rate != null && !canFl && (
          <div className="mt-1 text-xs opacity-85">
            <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Rate · </span>
            {INR(w.rate)}
          </div>
        )}
      </div>

      {/* Status update */}
      <div className="mt-4 bg-white rounded-2xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
        <div className="label-caps mb-2" style={{ color: ROYAL }}>Change status</div>
        <div className="grid grid-cols-2 gap-2" data-testid="staff-wo-status-grid">
          {statusChoices.map((s) => {
            const pal = statusPalette(s);
            const active = w.status === s;
            return (
              <button key={s} data-testid={`staff-wo-set-${s}`} onClick={() => updateStatus(s)} disabled={busy || active}
                className="h-11 rounded-xl text-xs font-semibold tracking-wide disabled:opacity-60 transition-all active:scale-[0.98]"
                style={{ background: active ? ROYAL : pal.bg, color: active ? CREAM : pal.fg, border: `1px solid ${active ? ROYAL : "transparent"}` }}>
                {s}
              </button>
            );
          })}
        </div>
      </div>

      {/* Comments */}
      <div className="mt-4 bg-white rounded-2xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
        <div className="label-caps mb-2" style={{ color: ROYAL }}>Comments</div>
        <ul className="space-y-2 max-h-64 overflow-auto" data-testid="staff-wo-comments">
          {comments.length === 0 && <li className="text-sm" style={{ color: `${ROYAL}80` }}>No comments yet.</li>}
          {comments.map((c) => (
            <li key={c.id} className="rounded-lg p-2" style={{ background: `${CREAM}`, border: `1px solid ${ROYAL}10` }}>
              <div className="text-[11px] font-mono-tight uppercase tracking-widest" style={{ color: `${ROYAL}80` }}>
                {c.user_name || "—"} · {fmtDate(c.created_at)}
              </div>
              <div className="text-sm mt-1" style={{ color: ROYAL }}>
                <RichText html={c.body_html} text={(c.body || "").replace(/^\[WO:[^\]]+\]\s*/, "")} />
              </div>
            </li>
          ))}
        </ul>
        <form onSubmit={addComment} className="mt-3 flex items-end gap-2" data-testid="staff-wo-comment-form">
          <Textarea data-testid="staff-wo-comment-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add a comment…" className="rounded-lg flex-1 focus-visible:ring-0" style={{ borderColor: `${ROYAL}40` }} />
          <button data-testid="staff-wo-comment-send" type="submit"
            className="h-10 px-3 rounded-full font-semibold text-sm inline-flex items-center gap-1"
            style={{ background: ROYAL, color: CREAM }}>
            <Send size={13} /> Post
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------------- Projects ---------------- */
export function StaffProjects() {
  const [projects, setProjects] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([])); }, []);
  if (projects === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  const statusOpts = ["all", "Planned", "Active", "In Progress", "On Hold", "Completed"];
  const list = filter === "all" ? projects : projects.filter((p) => p.status === filter);
  return (
    <div data-testid="staff-projects">
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 mb-2">
        {statusOpts.map((s) => (
          <button key={s} data-testid={`staff-project-filter-${s}`} onClick={() => setFilter(s)}
            className="text-[11px] font-mono-tight uppercase tracking-widest px-3 py-1.5 rounded-full whitespace-nowrap"
            style={{ background: filter === s ? ROYAL : "transparent", color: filter === s ? CREAM : ROYAL, border: `1px solid ${filter === s ? ROYAL : `${ROYAL}30`}` }}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>
      <ul className="space-y-2">
        {list.map((p) => <ProjectRow key={p.id} p={p} />)}
        {list.length === 0 && <li className="text-sm text-center py-8" style={{ color: `${ROYAL}80` }}>No projects.</li>}
      </ul>
    </div>
  );
}

export function StaffProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [wos, setWos] = useState([]);
  const [comments, setComments] = useState([]);
  const [note, setNote] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${id}`); setP(data);
      try { const { data: w } = await api.get("/work-orders", { params: { project_id: id } }); setWos(w); } catch {}
      try { const { data: cs } = await api.get(`/projects/${id}/comments`); setComments(cs); } catch {}
    } catch { toast.error("Could not load project"); nav("/app/projects"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addMsg = async (e) => {
    e.preventDefault();
    if (!note.trim()) return;
    try { await api.post(`/projects/${id}/comments`, { body: note.trim(), client_visible: false }); setNote(""); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  if (!p) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;

  const projectWOs = wos.filter((w) => !isFreelancer(user.role) || w.freelancer_id === user.linked_freelancer_id);

  return (
    <div data-testid="staff-project-detail">
      <div className="rounded-2xl p-4" style={{ background: ROYAL, color: CREAM, border: `1px solid ${GOLD}66` }}>
        <div className="label-caps" style={{ color: GOLD }}>{p.project_number || "Project"}</div>
        <div className="font-display text-xl mt-1 leading-tight">{p.name}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <StatusBadge status={p.status} testid={`staff-project-status-${p.id}`} />
          <span className="text-[11px] font-mono-tight uppercase tracking-widest opacity-80">Due {fmtDate(p.due_date)}</span>
        </div>
        {p.manager_name && (
          <div className="mt-2 text-xs opacity-85">
            <span className="font-mono-tight uppercase tracking-widest" style={{ color: GOLD }}>Manager · </span>
            {p.manager_name}
          </div>
        )}
      </div>

      <div className="mt-4">
        <PhaseTracker phases={p.phases || []} projectDueDate={p.due_date} projectStatus={p.status} testid="staff-phase-tracker" />
      </div>

      <Section title={`Work orders (${projectWOs.length})`}>
        {projectWOs.map((w) => <WorkOrderRow key={w.id} w={w} />)}
        {projectWOs.length === 0 && <EmptyLine text="No work orders on this project yet." />}
      </Section>

      {!isFreelancer(user.role) && (
        <div className="mt-5 bg-white rounded-2xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
          <div className="label-caps mb-2" style={{ color: ROYAL }}>Project messages</div>
          <ul className="space-y-2 max-h-64 overflow-auto" data-testid="staff-project-comments">
            {comments.length === 0 && <li className="text-sm" style={{ color: `${ROYAL}80` }}>No messages yet.</li>}
            {comments.map((c) => (
              <li key={c.id} className="rounded-lg p-2" style={{ background: CREAM, border: `1px solid ${ROYAL}10` }}>
                <div className="text-[11px] font-mono-tight uppercase tracking-widest" style={{ color: `${ROYAL}80` }}>
                  {c.user_name || "—"} · {fmtDate(c.created_at)}{c.client_visible ? " · visible to client" : ""}
                </div>
                <div className="text-sm mt-1" style={{ color: ROYAL }}><RichText html={c.body_html} text={c.body} /></div>
              </li>
            ))}
          </ul>
          <form onSubmit={addMsg} className="mt-3 flex items-end gap-2">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Write a project message…"
              className="rounded-lg flex-1 focus-visible:ring-0" style={{ borderColor: `${ROYAL}40` }}
              data-testid="staff-project-msg-input" />
            <button type="submit" className="h-10 px-3 rounded-full font-semibold text-sm inline-flex items-center gap-1"
              style={{ background: ROYAL, color: CREAM }} data-testid="staff-project-msg-send">
              <Send size={13} /> Post
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ---------------- Tickets ---------------- */
export function StaffTickets() {
  const [tickets, setTickets] = useState(null);
  const [projectsMap, setProjectsMap] = useState({});
  useEffect(() => {
    api.get("/tickets").then((r) => setTickets(r.data)).catch(() => setTickets([]));
    api.get("/projects").then((r) => setProjectsMap(Object.fromEntries(r.data.map((p) => [p.id, p])))).catch(() => {});
  }, []);
  if (tickets === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  return (
    <ul className="space-y-2" data-testid="staff-tickets">
      {tickets.map((t) => (
        <li key={t.id}>
          <Link to={`/app/tickets/${t.id}`} data-testid={`staff-ticket-${t.id}`}
            className="block bg-white rounded-xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>
                  {projectsMap[t.project_id]?.name || "Project"}
                </div>
                <div className="text-xs mt-0.5" style={{ color: `${ROYAL}CC` }}>{(t.description || "").slice(0, 100)}</div>
                <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-1" style={{ color: `${ROYAL}80` }}>
                  Due {fmtDate(t.deadline)}
                </div>
              </div>
              <StatusBadge status={t.closed ? "Closed" : t.status} />
            </div>
          </Link>
        </li>
      ))}
      {tickets.length === 0 && <li className="text-sm text-center py-8" style={{ color: `${ROYAL}80` }}>No tickets.</li>}
    </ul>
  );
}

export function StaffTicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [project, setProject] = useState(null);
  const [resp, setResp] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tickets");
      const found = data.find((x) => x.id === id);
      if (!found) { toast.error("Not found"); nav("/app/tickets"); return; }
      setT(found); setResp(found.response || "");
      try { const { data: p } = await api.get(`/projects/${found.project_id}`); setProject(p); } catch {}
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!t) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;

  const canFreelancer = isFreelancer(user.role);
  const canClose = !canFreelancer; // only manager+ can close

  const submitResponse = async (markCompleted) => {
    setBusy(true);
    try {
      const patch = { response: resp };
      if (canFreelancer && markCompleted) patch.status = "Resolved";
      if (canClose && markCompleted) { patch.status = "Closed"; patch.closed = true; }
      await api.patch(`/tickets/${id}`, patch);
      toast.success(markCompleted ? "Marked complete" : "Response saved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div data-testid="staff-ticket-detail">
      <div className="rounded-2xl p-4" style={{ background: ROYAL, color: CREAM, border: `1px solid ${GOLD}66` }}>
        <div className="label-caps" style={{ color: GOLD }}>Ticket</div>
        <div className="font-display text-lg mt-1 leading-tight">{project?.name || "Project"}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <StatusBadge status={t.closed ? "Closed" : t.status} />
          <span className="text-[11px] font-mono-tight uppercase tracking-widest opacity-80">Due {fmtDate(t.deadline)}</span>
        </div>
      </div>

      <div className="mt-4 bg-white rounded-2xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
        <div className="label-caps" style={{ color: ROYAL }}>Task description</div>
        <div className="text-sm mt-2 whitespace-pre-wrap" style={{ color: ROYAL }}>{t.description || "—"}</div>
        {t.instructions && (
          <>
            <div className="label-caps mt-4" style={{ color: ROYAL }}>Instructions</div>
            <div className="text-sm mt-2 whitespace-pre-wrap" style={{ color: `${ROYAL}CC` }}>{t.instructions}</div>
          </>
        )}
      </div>

      <div className="mt-4 bg-white rounded-2xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
        <div className="label-caps mb-2" style={{ color: ROYAL }}>
          {canFreelancer ? "Your response" : "Freelancer's response"}
        </div>
        <Textarea data-testid="staff-ticket-response" rows={5} value={resp} onChange={(e) => setResp(e.target.value)}
          placeholder={canFreelancer ? "Write your response…" : "No response yet"}
          disabled={!canFreelancer && !isAdmin(user.role) && !isManager(user.role)}
          className="rounded-lg focus-visible:ring-0" style={{ borderColor: `${ROYAL}40` }} />
        <div className="mt-3 flex gap-2">
          <button data-testid="staff-ticket-save" onClick={() => submitResponse(false)} disabled={busy}
            className="flex-1 h-10 rounded-full font-semibold text-sm disabled:opacity-60"
            style={{ background: `${ROYAL}10`, color: ROYAL, border: `1px solid ${ROYAL}30` }}>
            Save
          </button>
          <button data-testid="staff-ticket-complete" onClick={() => submitResponse(true)} disabled={busy || t.closed}
            className="flex-1 h-10 rounded-full font-semibold text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60"
            style={{ background: ROYAL, color: CREAM }}>
            <CheckCircle2 size={14} /> {canFreelancer ? "Mark complete" : "Close ticket"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Files (Freelancer: files from their work orders) ---------------- */
export function StaffFiles() {
  const [groups, setGroups] = useState(null);
  const { user } = useAuth();
  useEffect(() => {
    (async () => {
      try {
        const { data: wos } = await api.get("/work-orders");
        const pids = Array.from(new Set(wos.map((w) => w.project_id)));
        const { data: projects } = await api.get("/projects");
        const pmap = Object.fromEntries(projects.map((p) => [p.id, p]));
        const g = [];
        for (const pid of pids) {
          try {
            const { data: p } = await api.get(`/projects/${pid}`);
            const files = (p.files || []).filter((f) => !f.hidden);
            if (files.length) g.push({ project: pmap[pid] || p, files });
          } catch {}
        }
        setGroups(g);
      } catch { setGroups([]); }
    })();
  }, [user]);
  if (groups === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  if (groups.length === 0) return <div className="text-center py-10 text-sm" style={{ color: `${ROYAL}80` }} data-testid="staff-files-empty">No files on your projects yet.</div>;
  return (
    <div className="space-y-5" data-testid="staff-files">
      {groups.map((g) => (
        <section key={g.project.id}>
          <div className="label-caps mb-2" style={{ color: ROYAL }}>
            {g.project.project_number ? `${g.project.project_number} · ` : ""}{g.project.name}
          </div>
          <ul className="space-y-2">
            {g.files.map((f) => (
              <li key={f.id || f.url} className="bg-white rounded-xl p-3 flex items-center justify-between gap-3 border" style={{ borderColor: `${ROYAL}1a` }}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>{f.name || "Attachment"}</div>
                  <div className="text-[11px] font-mono-tight uppercase tracking-widest mt-0.5" style={{ color: `${ROYAL}80` }}>
                    {f.uploaded_at ? fmtDate(f.uploaded_at) : (f.created_at ? fmtDate(f.created_at) : "")}
                  </div>
                </div>
                {f.url && <a href={f.url} target="_blank" rel="noreferrer download"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full"
                  style={{ background: ROYAL, color: CREAM }}>Open</a>}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/* ---------------- Messages (Manager / Admin — across projects) ---------------- */
export function StaffMessages() {
  const [projects, setProjects] = useState(null);
  const [recent, setRecent] = useState({});
  useEffect(() => {
    (async () => {
      try {
        const { data: ps } = await api.get("/projects");
        setProjects(ps);
        const entries = await Promise.all(ps.slice(0, 20).map(async (p) => {
          try { const { data } = await api.get(`/projects/${p.id}/comments`); return [p.id, data.slice(-1)[0] || null]; }
          catch { return [p.id, null]; }
        }));
        setRecent(Object.fromEntries(entries));
      } catch { setProjects([]); }
    })();
  }, []);
  if (projects === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  return (
    <ul className="space-y-2" data-testid="staff-messages-list">
      {projects.map((p) => {
        const last = recent[p.id];
        return (
          <li key={p.id}>
            <Link to={`/app/projects/${p.id}`} className="block bg-white rounded-xl p-3 border" style={{ borderColor: `${ROYAL}1a` }}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate" style={{ color: ROYAL }}>{p.name}</div>
                  {last ? (
                    <div className="text-xs mt-1 truncate" style={{ color: `${ROYAL}CC` }}>
                      <span className="font-mono-tight uppercase tracking-widest text-[10px]" style={{ color: GOLD }}>
                        {last.user_name || "—"} ·{" "}
                      </span>
                      {last.body}
                    </div>
                  ) : (
                    <div className="text-xs mt-1" style={{ color: `${ROYAL}70` }}>No messages yet.</div>
                  )}
                </div>
                <ChevronRight size={14} style={{ color: `${ROYAL}80` }} />
              </div>
            </Link>
          </li>
        );
      })}
      {projects.length === 0 && <li className="text-sm text-center py-8" style={{ color: `${ROYAL}80` }}>No projects available.</li>}
    </ul>
  );
}

/* ---------------- Management Tasks (Admin only) ---------------- */
export function StaffTasks() {
  const [tasks, setTasks] = useState(null);
  const [filter, setFilter] = useState("Open");
  useEffect(() => { api.get("/management-tasks").then((r) => setTasks(r.data)).catch(() => setTasks([])); }, []);
  if (tasks === null) return <div className="text-sm" style={{ color: `${ROYAL}80` }}>Loading…</div>;
  const statusOpts = ["Open", "In Progress", "Completed", "all"];
  const list = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  return (
    <div data-testid="staff-tasks">
      <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-2 mb-2">
        {statusOpts.map((s) => (
          <button key={s} data-testid={`staff-task-filter-${s}`} onClick={() => setFilter(s)}
            className="text-[11px] font-mono-tight uppercase tracking-widest px-3 py-1.5 rounded-full whitespace-nowrap"
            style={{ background: filter === s ? ROYAL : "transparent", color: filter === s ? CREAM : ROYAL, border: `1px solid ${filter === s ? ROYAL : `${ROYAL}30`}` }}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>
      <ul className="space-y-2">
        {list.map((t) => <TaskRow key={t.id} t={t} />)}
        {list.length === 0 && <li className="text-sm text-center py-8" style={{ color: `${ROYAL}80` }}>No tasks.</li>}
      </ul>
    </div>
  );
}
